import React from "react";
import { Box, Container, Grid, Typography, Card } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { motion } from "framer-motion";
import { getIconByName } from "../navbars/CategoryIcons";

// Popular/hot categories first — ordered by demand & interest
const priorityOrder = [
  "Dogs", "Cats", "Snakes", "Geckos", "Leopard Geckos", "Monitor Lizards",
  "Frogs", "Tortoises", "Turtles", "Axolotl", "Birds", "Fish",
  "Tarantula", "Scorpions", "Hedgehog", "Chickens", "Chameleons",
  "Horses", "Goats", "Lizards",
];

const CategoryGrid = ({ categories }) => {
  const navigate = useNavigate();

  // Top-level groups
  const mainGroups = [
    {
      name: "Animals",
      icon: "https://cdn-icons-png.flaticon.com/512/10890/10890561.png",
      path: "/classes",
      bgImage: "https://images.unsplash.com/photo-1497752531616-c3afd9760a11?w=800&q=80",
    },
    {
      name: "Plants",
      icon: "https://cdn-icons-png.flaticon.com/512/45/45777.png",
      path: "/listings?search=plants",
      bgImage: "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800&q=80",
    },
    {
      name: "Minerals",
      icon: "https://cdn-icons-png.flaticon.com/512/11237/11237148.png",
      path: "/listings?search=minerals",
      bgImage: "https://images.unsplash.com/photo-1459411552884-841db9b3cc2a?w=800&q=80",
    },
    {
      name: "Services",
      icon: "https://cdn-icons-png.flaticon.com/512/4786/4786834.png",
      path: "/services",
      bgImage: "https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=800&q=80",
    },
  ];

  const activeCategories = (categories || []).filter((cat) => cat.active);

  // Sort by priority order, then alphabetically for the rest
  const sortedCategories = [...activeCategories].sort((a, b) => {
    const aIdx = priorityOrder.indexOf(a.name);
    const bIdx = priorityOrder.indexOf(b.name);
    if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
    if (aIdx !== -1) return -1;
    if (bIdx !== -1) return 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <Box sx={{ backgroundColor: "#111", py: { xs: 4, md: 6 } }}>
      <Container maxWidth="xl">
        {/* Section Title */}
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { xs: "22px", md: "30px" },
            mb: { xs: 2, md: 3 },
            color: "#fff",
          }}
        >
          Shop by Category
        </Typography>

        {/* Main Category Cards (4 big cards) */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {mainGroups.map((group, index) => (
            <Grid item xs={6} sm={3} key={group.name}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card
                  onClick={() => navigate(group.path)}
                  sx={{
                    position: "relative",
                    height: { xs: "140px", sm: "160px", md: "200px" },
                    cursor: "pointer",
                    borderRadius: "12px",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.3s ease",
                    border: "2px solid transparent",
                    "&:hover": {
                      transform: "scale(1.03)",
                      borderColor: "#FFC001",
                      boxShadow: "0 8px 25px rgba(255, 192, 1, 0.2)",
                    },
                  }}
                >
                  {/* Blurred background image */}
                  <Box
                    sx={{
                      position: "absolute",
                      inset: "-10px",
                      backgroundImage: `url(${group.bgImage})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      filter: "blur(4px) brightness(0.3)",
                    }}
                  />
                  {/* Content */}
                  <Box
                    component="img"
                    src={group.icon}
                    alt={group.name}
                    sx={{
                      width: { xs: "50px", md: "70px" },
                      height: { xs: "50px", md: "70px" },
                      objectFit: "contain",
                      mb: 1.5,
                      filter: "brightness(0) invert(1)",
                      position: "relative",
                      zIndex: 1,
                    }}
                  />
                  <Typography
                    sx={{
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: { xs: "16px", md: "20px" },
                      textAlign: "center",
                      position: "relative",
                      zIndex: 1,
                      textShadow: "0 2px 6px rgba(0,0,0,0.5)",
                    }}
                  >
                    {group.name}
                  </Typography>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Browse All Categories */}
        {sortedCategories.length > 0 && (
          <>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: { xs: "16px", md: "20px" },
                mb: 2,
                color: "#ccc",
              }}
            >
              Browse All Categories
            </Typography>
            <Grid container spacing={1.5}>
              {sortedCategories.slice(0, 18).map((cat, index) => (
                <Grid item xs={4} sm={3} md={2} key={cat.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.3 + index * 0.03 }}
                  >
                    <Card
                      onClick={() =>
                        navigate(`/listings?category_id=${cat.id}`)
                      }
                      sx={{
                        cursor: "pointer",
                        borderRadius: "10px",
                        overflow: "hidden",
                        position: "relative",
                        height: { xs: "130px", sm: "150px", md: "170px" },
                        transition: "all 0.3s ease",
                        border: "2px solid transparent",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          borderColor: "#FFC001",
                          boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                          "& .cat-img": {
                            transform: "scale(1.1)",
                          },
                        },
                      }}
                    >
                      {/* Background image */}
                      <Box
                        className="cat-img"
                        sx={{
                          position: "absolute",
                          inset: 0,
                          backgroundImage: `url(${cat.image || getIconByName(cat.name)})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          transition: "transform 0.4s ease",
                        }}
                      />
                      {/* Dark gradient overlay */}
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0.05) 100%)",
                        }}
                      />
                      {/* Rank badge for top 6 */}
                      {index < 6 && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 6,
                            left: 6,
                            backgroundColor: "#FFC001",
                            color: "#000",
                            fontWeight: 700,
                            fontSize: "10px",
                            px: 0.8,
                            py: 0.2,
                            borderRadius: "4px",
                            zIndex: 2,
                            lineHeight: 1.4,
                          }}
                        >
                          HOT
                        </Box>
                      )}
                      {/* Category name */}
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          right: 0,
                          p: { xs: 1, md: 1.5 },
                          zIndex: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: { xs: "12px", sm: "13px", md: "14px" },
                            textShadow: "0 1px 3px rgba(0,0,0,0.8)",
                            lineHeight: 1.2,
                          }}
                        >
                          {cat.name}
                        </Typography>
                      </Box>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>

            {/* See All link */}
            {sortedCategories.length > 18 && (
              <Box sx={{ textAlign: "center", mt: 2.5 }}>
                <Typography
                  onClick={() => navigate("/classes")}
                  sx={{
                    color: "#FFC001",
                    fontWeight: 600,
                    fontSize: "14px",
                    cursor: "pointer",
                    display: "inline-block",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  See All Categories ({sortedCategories.length})
                </Typography>
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

const mapStateToProps = ({ Categories }) => ({
  categories: Categories.classes,
});

export default connect(mapStateToProps)(CategoryGrid);
